import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { ArrowRight, Play } from 'lucide-react';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const CATEGORY_LABEL = {
    'dry-ice': 'Dry Ice Blasting',
    'laser': 'Laser Cleaning',
    'sandblasting': 'Sandblasting',
    'pressure-washing': 'Pressure Washing',
    'mixed': 'Mixed Services',
};

const ProjectCard = ({ p }) => {
    const cover = p.cover_image || p.images?.[0] || '';
    const hasVideo = (p.videos || []).length > 0;
    return (
        <Link
            to={`/projects/${p.slug || p.id}`}
            className="group block border border-white/10 hover:border-brand-primary/60 transition-colors bg-brand-surface"
            data-testid={`project-card-${p.slug || p.id}`}
        >
            <div className="aspect-[4/3] bg-white/[0.03] overflow-hidden relative">
                {cover ? (
                    <img src={cover} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30 label-caps text-xs">No image</div>
                )}
                {hasVideo && (
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 label-caps text-xs bg-black/60 backdrop-blur-sm border border-white/20 text-white px-2.5 py-1">
                        <Play className="h-3 w-3" /> Video
                    </div>
                )}
                <div className="absolute bottom-3 right-3 label-caps text-xs bg-brand-primary text-brand-ink px-2.5 py-1 font-bold">
                    {CATEGORY_LABEL[p.category] || p.category}
                </div>
            </div>
            <div className="p-5">
                <h3 className="font-display font-black uppercase text-xl leading-tight group-hover:text-brand-primary transition-colors">
                    {p.title}
                </h3>
                {p.summary && (
                    <p className="mt-2 text-sm text-white/70 leading-relaxed line-clamp-2">{p.summary}</p>
                )}
                <div className="mt-4 inline-flex items-center gap-2 label-caps text-brand-primary text-xs">
                    View case study <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
            </div>
        </Link>
    );
};

const Projects = () => {
    const [items, setItems] = useState(null);
    const [category, setCategory] = useState('all');

    useSEO({
        title: 'Project Gallery — Industrial Cleaning Case Studies in North Carolina',
        description: 'Real-world dry ice blasting, laser cleaning, sandblasting, and pressure washing projects delivered by Dry Ice Bulls across North Carolina — manufacturing, food processing, automotive, and heavy industry.',
    });

    useEffect(() => {
        api.get('/projects').then((r) => setItems(r.data)).catch(() => setItems([]));
    }, []);

    const filtered = (items || []).filter((p) => category === 'all' || p.category === category);
    const categories = ['all', 'dry-ice', 'laser', 'sandblasting', 'pressure-washing', 'mixed'];

    return (
        <div className="pt-24 pb-24" data-testid="projects-page">
            <div className="container-page">
                <div className="label-caps text-brand-primary mb-4">/ Project Gallery</div>
                <h1 className="font-display font-black uppercase text-5xl md:text-7xl leading-[0.95]">
                    Case Studies &<br /><span className="text-brand-primary">Recent Work</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">
                    Real-world industrial cleaning projects delivered across North Carolina — dry ice blasting, laser cleaning, sandblasting, and pressure washing for manufacturers, food processors, automotive, and heavy industry.
                </p>

                {items === null ? (
                    <div className="mt-16 text-white/40 label-caps text-sm">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="mt-16 border border-dashed border-white/10 p-10 text-center">
                        <div className="label-caps text-white/40 text-sm">
                            New projects will appear here soon. In the meantime, see our{' '}
                            <Link to="/services" className="text-brand-primary hover:underline">services</Link>
                            {' '}or{' '}
                            <Link to="/contact" className="text-brand-primary hover:underline">get in touch</Link>.
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Filter chips */}
                        <div className="mt-10 flex flex-wrap gap-2" data-testid="projects-filters">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCategory(c)}
                                    className={`label-caps text-xs px-4 py-2 border transition-colors ${
                                        category === c
                                            ? 'bg-brand-primary text-brand-ink border-brand-primary'
                                            : 'border-white/20 text-white/70 hover:text-white hover:border-white/50'
                                    }`}
                                    data-testid={`projects-filter-${c}`}
                                >
                                    {c === 'all' ? 'All' : (CATEGORY_LABEL[c] || c)}
                                </button>
                            ))}
                        </div>

                        {/* Grid */}
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
                            {filtered.map((p) => <ProjectCard key={p.id} p={p} />)}
                        </div>

                        {filtered.length === 0 && (
                            <div className="mt-8 text-white/40 label-caps text-sm">No projects in this category yet.</div>
                        )}
                    </>
                )}

                {/* CTA */}
                <CTASection
                    variant="project"
                    heading="Want results like these?"
                    subheading="Get the same dry ice blasting equipment — buy it, rent it, and get trained to run it."
                    context={{ trackingLocation: 'projects_bottom' }}
                    testid="projects-cta-bottom"
                    className="mt-16"
                />
            </div>
        </div>
    );
};

export default Projects;
